import { css } from 'aphrodite';
import { useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAutomationTemplateQuery } from '../../../queries/Automation';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { Button } from '../../components-v2/Button';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { AppDrawer, AppDrawerToolbar } from '../../components-v2/Drawer/AppDrawer';
import { AutomationCanvasEditor } from '../../../shared/components/automation/AutomationCanvasEditor';
import {
	IAutomationsReturnLocationState,
	resolveAutomationsReturnLocation,
} from '../../../shared/components/automation/automationsReturnLocation';
import { styleSheet } from './styles';

export function AutomationFlowEditorPage() {
	const { id: templateId } = useParams<{ id?: string }>();
	const history = useHistory();
	const location = useLocation<IAutomationsReturnLocationState | undefined>();

	const returnToAutomations = () => history.push(resolveAutomationsReturnLocation(location.state));

	const {
		data: template,
		isLoading,
		error,
		refetch,
	} = useAutomationTemplateQuery({
		id: templateId || '',
		enabled: !!templateId,
	});

	const pathComponentNameProvider = useCallback(
		(pathComponent: string) => {
			if (templateId && pathComponent === templateId) {
				return template?.name;
			}
			if (pathComponent === 'canvas') {
				return 'Edit Automation';
			}
			return null;
		},
		[templateId, template?.name]
	);

	return (
		<AutomationCanvasEditor
			templateId={templateId || ''}
			template={template}
			isLoading={isLoading}
			isError={!!error}
			error={error}
			refetch={refetch}
			onPublishSuccess={returnToAutomations}
			onDeleteSuccess={returnToAutomations}
			contentStyles={[styleSheet.contentExtras]}
			renderLoading={message => (
				<div className={css(styleSheet.layout)}>
					<div className={css(styleSheet.contentLoading)}>
						<div className={css(styleSheet.loadingContainer)} role='status' aria-live='polite'>
							<LoadingSpinner type='large' />
							<p className={css(styleSheet.loadingText)}>{message}</p>
						</div>
					</div>
				</div>
			)}
			renderError={(message, onRetry) => (
				<div className={css(styleSheet.layout)}>
					<div className={css(styleSheet.contentLoading)}>
						<div className={css(styleSheet.errorContainer)} role='alert' aria-live='assertive'>
							<h2 className={css(styleSheet.errorTitle)}>Failed to load automation</h2>
							<p className={css(styleSheet.errorMessage)}>{message}</p>
							<Button variant='primary' onClick={onRetry} aria-label='Retry loading automation'>
								Retry
							</Button>
						</div>
					</div>
				</div>
			)}
		>
			{({ contentArea, drawerPanels, drawerToolbarButtons }) => (
				<>
					<MultiContainerHeader
						appBarHeader={<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />}
					/>
					<div className={css(styleSheet.layout)}>
						{contentArea}
						<AppDrawer>
							{drawerPanels}
							<AppDrawerToolbar>{drawerToolbarButtons}</AppDrawerToolbar>
						</AppDrawer>
					</div>
				</>
			)}
		</AutomationCanvasEditor>
	);
}
